import { useQuery } from '@tanstack/react-query'
import PageHeader from '../components/PageHeader'
import { releaseLogs } from '../api/client'
import { PageLayout, Card, Badge } from '../components/ui'

function RocketIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  )
}

function BugIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  )
}

function WrenchIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  )
}

function getReleaseTypeInfo(type) {
  switch (type) {
    case 'feature':
      return {
        icon: RocketIcon,
        label: 'Feature',
        variant: 'success',
        colorClass: 'text-lume-teal dark:text-lume-teal',
        bgClass: 'bg-lume-teal/20',
      }
    case 'bugfix':
      return {
        icon: BugIcon,
        label: 'Bug Fix',
        variant: 'warning',
        colorClass: 'text-status-warning',
        bgClass: 'bg-status-warning/20',
      }
    case 'maintenance':
      return {
        icon: WrenchIcon,
        label: 'Maintenance',
        variant: 'info',
        colorClass: 'text-status-info',
        bgClass: 'bg-status-info/20',
      }
    default:
      return {
        icon: RocketIcon,
        label: type,
        variant: 'default',
        colorClass: 'text-indices-muted',
        bgClass: 'bg-dial-elevated',
      }
  }
}

function formatDate(dateString) {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

function ReleaseCard({ release }) {
  const typeInfo = getReleaseTypeInfo(release.releaseType)
  const TypeIcon = typeInfo.icon

  return (
    <div className="relative pl-8">
      {/* Timeline dot */}
      <div className={`absolute left-0 top-2 w-4 h-4 rounded-full ${typeInfo.bgClass} flex items-center justify-center`}>
        <div className={`w-2 h-2 rounded-full ${typeInfo.colorClass.replace('text-', 'bg-')}`} />
      </div>

      {/* Timeline line */}
      <div className="absolute left-[7px] top-6 bottom-0 w-0.5 bg-dial-border" />

      <Card className="mb-4">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-lg ${typeInfo.bgClass} flex items-center justify-center flex-shrink-0`}>
            <TypeIcon className={`w-5 h-5 ${typeInfo.colorClass}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-mono font-bold text-lume-teal dark:text-lume-teal">
                v{release.version}
              </span>
              <Badge variant={typeInfo.variant} size="sm">
                {typeInfo.label}
              </Badge>
            </div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-indices-bright mt-1 font-display">
              {release.title}
            </h3>
            <p className="text-sm text-gray-600 dark:text-indices-medium mt-2 whitespace-pre-wrap">
              {release.description}
            </p>
            <p className="text-xs text-gray-400 dark:text-indices-muted mt-3">
              {formatDate(release.releaseDate)}
            </p>
          </div>
        </div>
      </Card>
    </div>
  )
}

export default function ReleaseLogPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['releaseLogs'],
    queryFn: releaseLogs.list,
  })

  const releases = data?.releases || []

  return (
    <PageLayout>
      <PageHeader title="Release Log" showBack />

      <div className="px-4 py-6">
        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lume-teal" />
          </div>
        ) : error ? (
          <Card>
            <div className="text-center py-8">
              <p className="text-status-error">Failed to load releases</p>
            </div>
          </Card>
        ) : releases.length === 0 ? (
          <Card>
            <div className="text-center py-12">
              <RocketIcon className="w-12 h-12 mx-auto text-indices-muted mb-4" />
              <p className="text-gray-500 dark:text-indices-muted">No releases yet</p>
              <p className="text-sm text-gray-400 dark:text-indices-muted mt-1">
                Release notes will appear here as new features are added
              </p>
            </div>
          </Card>
        ) : (
          <div className="relative">
            {releases.map((release, index) => (
              <div key={release.id || index} className={index === releases.length - 1 ? '[&>div:last-child]:hidden' : ''}>
                <ReleaseCard release={release} />
              </div>
            ))}
          </div>
        )}
      </div>
    </PageLayout>
  )
}
